/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-organization-conf-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import React, { useEffect, useState } from 'react';

import { useBoolean, useTranslation } from 'hooks';
import { GET_SYSTEM_ORGANISATIONS, SAVE_ORGANISATION_CONFIGURATION } from 'queries/org-admin';
import { GET_ORGANISATION } from 'queries/uam';
import NotificationService from 'utils/notification';
import { validateFields, checkIfNameExist } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import CopyButton from 'views/components/core/copy-button';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface EditOrganizationConfDialogInterface {
    open: boolean;
    close(action?: string): any;
    currentOrganization: any;
    organizations?: any[];
}

const EditOrganizationConfDialog = ({
    open,
    close,
    currentOrganization,
    organizations,
}: EditOrganizationConfDialogInterface) => {
    const { t } = useTranslation();
    const initialState = {
        name: '',
        region: '',
        loginCode: '',
        connection: '',
        useTransactions: false,
        apiKey: '',
        config: null as any,
        recordId: '',
        created: '',
        updated: '',
        createdBy: '',
        updatedBy: '',
        key: '',
    };

    const initialError = { name: '', loginCode: '' };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialError });
    const disabledSaveButton = useBoolean(true);
    const [loadOrganization, { data }] = useLazyQuery(GET_ORGANISATION);
    const [saveConfiguration] = useMutation(SAVE_ORGANISATION_CONFIGURATION);

    useEffect(() => {
        if (currentOrganization) loadOrganization({ variables: { key: currentOrganization.key } });
    }, [currentOrganization]);

    useEffect(() => {
        if (data?.organisation) {
            const fullOrganisation = data.organisation;
            setState({
                ...currentOrganization,
                updated: fullOrganisation.updated,
                updatedBy: fullOrganisation.updatedBy,
                created: fullOrganisation.created,
                createdBy: fullOrganisation.createdBy,
                recordId: fullOrganisation.recordId,
            });
        } else
            setState({
                ...currentOrganization,
            });
    }, [data]);

    const onClose = (action?: string) => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialError });
        close(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        let error: any = {};
        if (field === 'loginCode') {
            error = { loginCode: '' };
            const regexp = /^[A-Za-z0-9-_]+$/;
            if (value.length && !regexp.test(value)) error.loginCode = t('admin:pathShouldBeValid');
            else error = validateFields(value, field, length, required);
        } else {
            error = validateFields(value, field, length, required);
        }
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string, length?: number, required?: boolean) => (event: any) => {
        const { value } = event.target;
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: value });

        validate(value, fieldName, length, required);
    };

    const checkRequiredFields = (fields: string[]) => {
        let isValid = true;
        let newErrors = { ...errors };
        fields.forEach((field) => {
            const error = validateFields((state as any)[field], field, 0, true);
            newErrors = {
                ...newErrors,
                ...error,
            };
        });
        if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) {
            isValid = false;
        }
        setErrors({ ...newErrors });
        return isValid;
    };

    const checkForName = () => {
        const value: any = {
            ...state,
            name: state.name.trim(),
            id: currentOrganization?.id ?? null,
        };
        const resultName = checkIfNameExist(value, organizations, 'name');
        const resultURLPath = checkIfNameExist(value, organizations, 'loginCode');
        if (resultName) {
            setErrors({ ...errors, name: t('admin:orgAlreadyExists') });
        } else if (resultURLPath) {
            setErrors({ ...errors, loginCode: t('admin:pathAlreadyExists') });
        }
        return resultName || resultURLPath;
    };

    const checkForErrors = () => Object.keys(errors).filter((item) => (errors as any)[item]).length > 0;

    const successRequest = () => {
        NotificationService.showNotification(t('admin:organizationSuccessfulUpdated', { id: state.recordId }));
        onClose('add');
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const submit = () => {
        if (!checkForErrors() && checkRequiredFields(['name']) && !checkForName()) {
            disabledSaveButton.setTrue();
            saveConfiguration({
                variables: {
                    organisationKey: currentOrganization.key,
                    name: state.name,
                    urlPath: state.loginCode,
                },
                refetchQueries: [
                    {
                        query: GET_SYSTEM_ORGANISATIONS,
                    },
                ],
            })
                .then(() => {
                    successRequest();
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    return (
        <>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open}
                >
                    <DialogTitle id="edit-organization-modal-title">
                        {t('admin:editOrganizationConfiguration', { id: state?.recordId })}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="edit-organization-dialog-content">
                        <div className="value-list">
                            <p>
                                <span>{`${t('admin:DBRegion')}: `}</span>
                                {t(`admin:${state?.config?.mongoRegion}`)}
                            </p>
                            {state.config?.custom ? (
                                <p>
                                    <span>{`${t('admin:useTransaction')}: `}</span>
                                    {state.config.custom.useTransactions ? t('yes') : t('no')}
                                </p>
                            ) : null}
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    label={`${t('admin:organization')}*`}
                                    id="edit-organization-dialog-name"
                                    fullWidth
                                    value={state.name}
                                    onChange={onChange('name', 150)}
                                    placeholder={t('admin:enterName')}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-organization-dialog-name-help-text"
                                            length={state.name?.length}
                                            max={150}
                                            error={errors.name}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.name}
                                    className="before-select"
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <TextField
                                    label={`${t('admin:URLPath')}`}
                                    id="edit-organization-dialog-path"
                                    fullWidth
                                    value={state.loginCode}
                                    onChange={onChange('loginCode', 20)}
                                    placeholder={t('admin:enterTheUrlPath')}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-organization-dialog-path-help-text"
                                            length={state.loginCode?.length}
                                            max={20}
                                            error={errors.loginCode}
                                        />
                                    }
                                    variant="outlined"
                                    InputProps={{ endAdornment: <CopyButton value={state.loginCode} /> }}
                                    color="secondary"
                                    error={!!errors.loginCode}
                                    className="before-select"
                                />
                            </FormControl>
                        </div>
                        {state.config?.custom ? (
                            <div className="type-edc-wrapper">
                                <TextField
                                    id="edit-organization-dialog-connection"
                                    label={t('admin:DBConnectionString')}
                                    fullWidth
                                    value={state.connection}
                                    size="small"
                                    placeholder={t('admin:enterTheConnectionString')}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: <CopyButton value={state.connection} />,
                                    }}
                                    variant="outlined"
                                    color="secondary"
                                />
                            </div>
                        ) : null}
                        <div className="type-edc-wrapper">
                            <TextField
                                readOnly
                                id="edit-organization-dialog-apiKey"
                                label={t('admin:APIKey')}
                                fullWidth
                                value={state.apiKey}
                                size="small"
                                placeholder={t('admin:EnterAPIKey')}
                                InputProps={{ readOnly: true, endAdornment: <CopyButton value={state.apiKey} /> }}
                                variant="outlined"
                                color="secondary"
                            />
                        </div>
                        <div className="type-edc-wrapper">
                            <TextField
                                readOnly
                                id="edit-organization-dialog-keyId"
                                label={t('admin:keyId')}
                                fullWidth
                                value={state.key}
                                size="small"
                                InputProps={{ readOnly: true, endAdornment: <CopyButton value={state.key} /> }}
                                variant="outlined"
                                color="secondary"
                            />
                        </div>
                        <DialogActions
                            style={{ paddingBottom: 25, paddingTop: 8 }}
                            className="actions-info-wrapper-edit-cv flex-end"
                        >
                            <Button
                                disabled={
                                    disabledSaveButton.value ||
                                    Object.keys(errors).filter((item) => (errors as any)[item]).length > 0
                                }
                                className="edit-organization-btn"
                                text="update"
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default EditOrganizationConfDialog;
